import { OccurrenceDispatchDto, OccurrenceDto } from "../dtos/occurrenceDto";
import { OccurrenceStepEnum } from "../enums/occurrenceStep";
import { Occurrence, OccurrenceDispatch, OccurrenceReview } from "../models/occurrence";
import { createOccurrenceJson, occurrenceToJson } from "../components/kanban/components/form/utils/occurrence-to-json";

import ApiService from "../services/api";
import { fetchRoutingUserProfileId } from "./fetchUser";

export const createOccurrence = async (occurrence: Occurrence): Promise<{ data?: Occurrence; error?: string; errorId?: string }> => {
    const apiService = new ApiService();
    try {
        // Get the user profile ID from the routing API
        const createdByUserProfileId = await fetchRoutingUserProfileId();

        const response = await apiService.post(`/occurrence`, createOccurrenceJson({...occurrence, createdByUserProfileId}));
        if (response.status >= 200 && response.status < 300) {
            return { data: OccurrenceDto(response.data) };
        } else return { error: 'Erro ao criar ocorrência', errorId: 'intl-msg:shared.apiError.occurrence.create' };
    } catch (error: any) {
        console.error(error);
        if (error?.status === 422) {
            return { error: error.response.data?.message || 'Erro de validação', errorId: error.response.data?.message ? undefined : 'intl-msg:shared.apiError.occurrence.validation' };
        }
        return { error: 'Erro ao criar ocorrência', errorId: 'intl-msg:shared.apiError.occurrence.create' };
    }
}

export const updateOccurrence = async (occurrence: Occurrence): Promise<{ data?: Occurrence; conflict?: boolean }> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.put(`/occurrence/${occurrence.id}`, occurrenceToJson(occurrence));
        if (response.status === 200) {
            return { data: OccurrenceDto(response.data) };
        } else return {};
    } catch (error: any) {
        console.error(error);
        if (error?.response?.status === 409) {
            return { conflict: true };
        }
        return {};
    }
}

export const changeOccurrenceStep = async (occurrence: Occurrence, fromStepId: number, toStep: OccurrenceStepEnum): Promise<Occurrence | undefined> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.put(`/occurrence/${occurrence.id}/step/${fromStepId}/to/${toStep}`, {});
        if (response.status === 200) {
            return OccurrenceDto(response.data);
        } else {
            return undefined;
        }
    } catch (error: any) {
        if (error?.response?.data) {
            throw error.response.data;
        }
        throw error;
    }
}

export const finishOccurrence = async (
    occurrence: Occurrence, 
    finalizationReasonType?: string, 
    finalizationReasonDescription?: string,
    fromStepId?: number,
): Promise<void> => {
    const apiService = new ApiService();
    let payload: { finalizationReasonType?: string, finalizationReasonDescription?: string, fromStepId?: number } = {};
    
    if (finalizationReasonType) {
        payload.finalizationReasonType = finalizationReasonType;
    }
    
    if (finalizationReasonDescription) {
        payload.finalizationReasonDescription = finalizationReasonDescription;
    }

    if (fromStepId) {
        payload.fromStepId = fromStepId;
    }
    
    try {
        const response = await apiService.put(`/occurrence/${occurrence.id}/finalized`, payload);
        
        if (response.status > 201) {
            throw new Error('Erro ao finalizar ocorrência. Por favor, tente novamente ou entre em contato com o suporte.');
        }
    } catch (error: any) {
        // Verifica se é um erro HTTP com dados na resposta
        if (error?.response?.data) {
            // Lança o erro com os dados da resposta (code, message)
            throw error.response.data;
        }
        // Se não tem dados específicos, lança o erro original
        throw error;
    }
}

export const reviewOccurrenceStep = async (occurrenceId: string, review: OccurrenceReview): Promise<void> => {
    const apiService = new ApiService();
    await apiService.post(`/occurrence/review/${occurrenceId}/${review.step}`, review);
}

export const getOccurrenceReview = async (occurrenceId: string): Promise<OccurrenceReview[]> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.get<any[]>(`/occurrence/review/${occurrenceId}`);
        if (response.status === 200) {
        return (response.data ?? []).map((review: any) => ({
            id: review.uuid,
            step: review.step,
            rate: review.rate,
            comment: review.comment,
        }));
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const createOccurrenceDispatch = async (occurrenceId: string, dispatch: OccurrenceDispatch): Promise<OccurrenceDispatch | undefined> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.post(`/occurrence/${occurrenceId}/dispatch`, dispatch);
        if (response.status === 200) {
            return OccurrenceDispatchDto(response.data);
        } else return undefined;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

export const updateOccurrenceDispatch = async (occurrenceId: string, dispatch: OccurrenceDispatch): Promise<OccurrenceDispatch | undefined> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.put(`/occurrence/${occurrenceId}/dispatch/${dispatch.id}`, dispatch);
        if (response.status === 200) {
            return OccurrenceDispatchDto(response.data);
        } else return undefined;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

export const cancelOccurrenceDispatch = async (occurrenceId: string, dispatchId: string, data: {reason: string, description: string}): Promise<OccurrenceDispatch | undefined> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.put(`/occurrence/${occurrenceId}/dispatch/${dispatchId}/cancel`, { ...data });
        if (response.status === 204) {
            return OccurrenceDispatchDto(response.data);
        } else return undefined;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

export const removeOccurrenceDispatch = async (occurrenceId: string, dispatchId: string): Promise<void> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.delete(`/occurrence/${occurrenceId}/dispatch/${dispatchId}`);
        if (response.status === 204) {
            return;
        } else return undefined;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

export const makeOccurrenceDispatchAvailable = async (occurrenceId: string, dispatchId: string): Promise<OccurrenceDispatch | undefined> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.put(`/occurrence/${occurrenceId}/dispatch/${dispatchId}/available`, {});
        if (response.status === 200) {
            return OccurrenceDispatchDto(response.data);
        } else return undefined;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

export const assignDriverToOccurrenceDispatch = async (occurrenceId: string, dispatchId: string, driverName: string): Promise<OccurrenceDispatch | undefined> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.put(`/occurrence/${occurrenceId}/dispatch/${dispatchId}/driver`, { 'driver': driverName });
        if (response.status === 200) {
            return OccurrenceDispatchDto(response.data);
        } else return undefined;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

export const getOccurrenceDispatchDocument = async (chassis: string, dispatchId: string): Promise<string | undefined> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.get<any[]>(`/attachment/occurrence-dispatches/${chassis}`);
        if (response.status === 200) {
            return response.data?.find((file: any) => file.name === `${dispatchId}.pdf`)?.link;
        } else return undefined;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

export const checkChassisAvailability = async (chassis: string): Promise<boolean> => {
    const apiService = new ApiService();
    try {
        const response = await apiService.get<{exists: boolean, message?: string}>(`/occurrence/validate-chassis/${chassis}`);
        if (response.status === 200) {
            return response.data.exists;
        } else return false;
    } catch (error) {
        console.error(error);
        return false;
    }
}